import React, { useEffect } from 'react';
import { maskEngineChassisNumber, maskChassisNumber } from '../utils/maskUtils';

const Receipt = ({ coverType, selectedClass, startDate, endDate, coverPeriod, insuredData, vehicleData, premium, clientId, certificateStatus, certificateLastError, onNewApplication, onRetrySave, onRetryCertificate }) => {
  
  // Generate receipt code: MON/date/registration
  const generateReceiptCode = () => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0].replace(/-/g, ''); // YYYYMMDD format
    const regNumber = vehicleData?.registrationNumber?.replace(/[^A-Z0-9]/g, '') || 'UNKNOWN';
    return `MON/${dateStr}/${regNumber}`;
  };

  const downloadReceipt = () => {
    // Create receipt content as HTML
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Insurance Receipt - ${receiptCode}</title>
        <style>
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            margin: 0; 
            padding: 20px; 
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            min-height: 100vh;
          }
          .receipt { 
            max-width: 800px; 
            margin: 0 auto; 
            background: white;
            border-radius: 20px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
            overflow: hidden;
          }
          .header { 
            text-align: center; 
            background: linear-gradient(135deg, #064e3b 0%, #065f46 100%); 
            color: white; 
            padding: 40px 30px;
            position: relative;
          }
          .header::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
            opacity: 0.3;
          }
          .company-logo {
            position: relative;
            z-index: 1;
          }
          .company-logo h1 { 
            font-size: 2.5rem; 
            font-weight: 800; 
            margin: 0 0 15px 0; 
            color: white;
            text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          }
          .receipt-title h2 { 
            font-size: 2rem; 
            font-weight: 700; 
            margin: 0 0 10px 0; 
            color: white;
            text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          }
          .receipt-subtitle { 
            font-size: 1.1rem; 
            opacity: 0.95; 
            margin: 0;
            font-weight: 300;
          }
          .receipt-content { 
            padding: 40px; 
          }
          .receipt-section { 
            margin-bottom: 35px; 
          }
          .receipt-section h3 { 
            font-size: 1.4rem; 
            font-weight: 700; 
            color: #064e3b; 
            margin-bottom: 20px; 
            padding-bottom: 12px; 
            border-bottom: 3px solid #e5e7eb;
            position: relative;
          }
          .receipt-section h3::after {
            content: '';
            position: absolute;
            bottom: -3px;
            left: 0;
            width: 60px;
            height: 3px;
            background: linear-gradient(90deg, #064e3b, #10b981);
          }
          .receipt-grid { 
            display: grid; 
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); 
            gap: 15px; 
          }
          .receipt-item { 
            display: flex; 
            justify-content: space-between; 
            align-items: center; 
            padding: 18px 20px; 
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            border-radius: 12px; 
            border: 1px solid #e2e8f0;
            transition: all 0.3s ease;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          }
          .receipt-item:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0,0,0,0.1);
            border-color: #cbd5e1;
          }
          .receipt-label { 
            font-weight: 600; 
            color: #374151; 
            font-size: 0.9rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .receipt-value { 
            font-weight: 700; 
            color: #064e3b; 
            font-size: 0.95rem; 
          }
          .receipt-code { 
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); 
            color: white; 
            padding: 8px 16px; 
            border-radius: 8px; 
            font-family: 'Courier New', monospace; 
            font-weight: 800;
            font-size: 1rem;
            box-shadow: 0 4px 12px rgba(217, 119, 6, 0.3);
          }
          .payment-success { 
            background: linear-gradient(135deg, #10b981 0%, #059669 100%); 
            color: white; 
            padding: 8px 16px; 
            border-radius: 8px; 
            font-weight: 800;
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
          }
          .payment-details { 
            background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); 
            border: 2px solid #bbf7d0; 
            border-radius: 20px; 
            padding: 25px;
            box-shadow: 0 8px 25px rgba(16, 185, 129, 0.15);
          }
          .payment-summary { 
            display: flex; 
            flex-direction: column; 
            gap: 15px; 
          }
          .payment-row { 
            display: flex; 
            justify-content: space-between; 
            align-items: center; 
            padding: 18px 20px; 
            background: white; 
            border-radius: 12px; 
            border: 1px solid #d1fae5;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          }
          .payment-label { 
            font-weight: 600; 
            color: #374151; 
            font-size: 0.95rem;
          }
          .payment-amount { 
            font-weight: 800; 
            color: #000000; 
            font-size: 1.3rem;
            background: linear-gradient(135deg, #064e3b, #10b981);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
          .payment-value { 
            font-weight: 600; 
            color: #065f46; 
            font-size: 0.95rem;
          }
          .delivery-info { 
            display: flex; 
            align-items: flex-start; 
            gap: 20px; 
            background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); 
            border: 2px solid #10b981; 
            border-radius: 16px; 
            padding: 25px;
            box-shadow: 0 8px 25px rgba(16, 185, 129, 0.15);
          }
          .delivery-icon { 
            font-size: 2rem; 
            flex-shrink: 0;
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
          }
          .delivery-message { 
            font-size: 1rem; 
            line-height: 1.7; 
            color: #047857; 
            font-weight: 500;
          }
          .delivery-message strong { 
            color: #064e3b; 
            font-weight: 700;
          }
          .receipt-footer { 
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); 
            border-radius: 20px; 
            padding: 25px; 
            margin-top: 30px;
            border: 1px solid #e2e8f0;
          }
          .footer-note p { 
            font-weight: 600; 
            color: #064e3b; 
            margin-bottom: 15px;
            font-size: 1.1rem;
          }
          .footer-note ul { 
            margin: 0; 
            padding-left: 20px; 
          }
          .footer-note li { 
            margin-bottom: 8px; 
            color: #374151; 
            line-height: 1.6;
            font-size: 0.95rem;
          }
          .receipt-actions { 
            display: flex; 
            justify-content: center; 
            gap: 20px; 
            padding: 30px; 
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); 
            border-top: 1px solid #e2e8f0; 
          }
          .btn-secondary, .btn-primary {
            padding: 15px 30px;
            border: none;
            border-radius: 12px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            text-decoration: none;
            display: inline-block;
            text-align: center;
          }
          .btn-secondary {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            color: white;
            box-shadow: 0 4px 12px rgba(217, 119, 6, 0.3);
          }
          .btn-secondary:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(217, 119, 6, 0.4);
          }
          .btn-primary {
            background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
            color: white;
            box-shadow: 0 4px 12px rgba(6, 78, 59, 0.3);
          }
          .btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(6, 78, 59, 0.4);
          }
          @media print {
            body { background: white; }
            .receipt { box-shadow: none; border-radius: 0; }
            .receipt-actions { display: none; }
            .btn-secondary, .btn-primary { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <div class="company-logo">
              <h1>🏛️ The Monarch Insurance Company</h1>
            </div>
            <div class="receipt-title">
              <h2>Insurance Policy Receipt</h2>
              <p class="receipt-subtitle">Payment Confirmation & Policy Details</p>
            </div>
          </div>
          
          <div class="receipt-content">
            <div class="receipt-section">
              <h3>📋 Receipt Information</h3>
              <div class="receipt-grid">
                <div class="receipt-item">
                  <span class="receipt-label">Client ID</span>
                  <span class="receipt-value client-id">${clientId || 'N/A'}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Receipt Code</span>
                  <span class="receipt-value receipt-code">${receiptCode}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Issue Date</span>
                  <span class="receipt-value">${currentDate}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Issue Time</span>
                  <span class="receipt-value">${currentTime}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Payment Status</span>
                  <span class="receipt-value payment-success">✅ PAID</span>
                </div>
              </div>
            </div>
            
            <div class="receipt-section">
              <h3>👤 Insured Person Details</h3>
              <div class="receipt-grid">
                <div class="receipt-item">
                  <span class="receipt-label">ID Number</span>
                  <span class="receipt-value">${insuredData?.idNumber || 'N/A'}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Full Name</span>
                  <span class="receipt-value">${insuredData?.insuredName || 'N/A'}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Phone Number</span>
                  <span class="receipt-value">${insuredData?.phoneNumber || 'N/A'}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Email Address</span>
                  <span class="receipt-value">${insuredData?.emailAddress || 'N/A'}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">KRA PIN</span>
                  <span class="receipt-value">${insuredData?.kraPin || 'N/A'}</span>
                </div>
              </div>
            </div>
            
            <div class="receipt-section">
              <h3>🚗 Vehicle Information</h3>
              <div class="receipt-grid">
                <div class="receipt-item">
                  <span class="receipt-label">Registration Number</span>
                  <span class="receipt-value">${vehicleData?.registrationNumber || 'N/A'}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Vehicle Make</span>
                  <span class="receipt-value">${vehicleData?.make || 'N/A'}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Year of Manufacture</span>
                  <span class="receipt-value">${vehicleData?.yearOfManufacture || 'N/A'}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Chassis Number</span>
                  <span class="receipt-value">${vehicleData?.chassisNumber ? maskChassisNumber(String(vehicleData.chassisNumber)) : 'N/A'}</span>
                </div>
                ${vehicleData?.engineNumber ? `
                <div class="receipt-item">
                  <span class="receipt-label">Engine Number</span>
                  <span class="receipt-value">${maskEngineChassisNumber(String(vehicleData.engineNumber))}</span>
                </div>
                ` : ''}
              </div>
            </div>
            
            <div class="receipt-section">
              <h3>🛡️ Insurance Coverage Details</h3>
              <div class="receipt-grid">
                <div class="receipt-item">
                  <span class="receipt-label">Cover Type</span>
                  <span class="receipt-value">${coverType}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Vehicle Class</span>
                  <span class="receipt-value">${selectedClass}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Cover Period</span>
                  <span class="receipt-value">${coverPeriod}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">Start Date</span>
                  <span class="receipt-value">${startDate}</span>
                </div>
                <div class="receipt-item">
                  <span class="receipt-label">End Date</span>
                  <span class="receipt-value">${endDate}</span>
                </div>
              </div>
            </div>
            
            <div class="receipt-section payment-details">
              <h3>💰 Payment Information</h3>
              <div class="payment-summary">
                <div class="payment-row">
                  <span class="payment-label">Premium Amount</span>
                  <span class="payment-amount">KES ${premium?.amount.toLocaleString()}</span>
                </div>
                <div class="payment-row">
                  <span class="payment-label">Payment Method</span>
                  <span class="payment-value">MPESA Mobile Money</span>
                </div>
                <div class="payment-row">
                  <span class="payment-label">Transaction Date</span>
                  <span class="payment-value">${currentDate} at ${currentTime}</span>
                </div>
              </div>
            </div>
            
            {/* WhatsApp delivery section commented out - we are not delivering via WhatsApp
            <div class="receipt-section">
              <h3>📱 Certificate Delivery</h3>
              <div class="delivery-info">
                <div class="delivery-icon">📱</div>
                <div class="delivery-message">
                  <strong>WhatsApp Delivery:</strong> Your insurance certificate will be sent to <strong>${insuredData?.phoneNumber || 'N/A'}</strong> within 2-3 minutes.
                </div>
              </div>
            </div>
            */}
            
            <div class="receipt-footer">
              <div class="footer-note">
                <p><strong>Important Notes:</strong></p>
                <ul>
                  <li>Keep this receipt for your records</li>
                  <li>Your policy is active from ${startDate} to ${endDate}</li>
                  <li>For any queries, contact our customer service</li>
                  <li>Receipt Code: <strong>${receiptCode}</strong></li>
                </ul>
              </div>
            </div>
          </div>
          
          <div class="receipt-actions">
            <button class="btn-secondary" onclick="window.print()">🖨️ Print Receipt</button>
            <button class="btn-primary" onclick="window.close()">✖️ Close</button>
          </div>
        </div>
      </body>
      </html>
    `;

    // Create blob and download
    const blob = new Blob([receiptHTML], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Insurance_Receipt_${receiptCode}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const receiptCode = generateReceiptCode();
  const currentDate = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const currentTime = new Date().toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  return (
    <div className="receipt">
      <div className="receipt-header">
        <div className="company-logo">
          <h1>🏛️ The Monarch Insurance Company</h1>
        </div>
        <div className="receipt-title">
          <h2>Insurance Policy Receipt</h2>
          <p className="receipt-subtitle">Payment Confirmation & Policy Details</p>
        </div>
      </div>

      <div className="receipt-content">
        <div className="receipt-section">
          <h3>📋 Receipt Information</h3>
          <div className="receipt-grid">
            <div className="receipt-item">
              <span className="receipt-label">Client ID:</span>
              <span className="receipt-value client-id">{clientId || 'N/A'}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Receipt Code:</span>
              <span className="receipt-value receipt-code">{receiptCode}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Issue Date:</span>
              <span className="receipt-value">{currentDate}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Issue Time:</span>
              <span className="receipt-value">{currentTime}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Payment Status:</span>
              <span className="receipt-value payment-success">✅ PAID</span>
            </div>
          </div>
        </div>

        <div className="receipt-section">
          <h3>👤 Insured Person Details</h3>
          <div className="receipt-grid">
            <div className="receipt-item">
              <span className="receipt-label">ID Number:</span>
              <span className="receipt-value">{insuredData?.idNumber}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Full Name:</span>
              <span className="receipt-value">{insuredData?.insuredName}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Phone Number:</span>
              <span className="receipt-value">{insuredData?.phoneNumber}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Email Address:</span>
              <span className="receipt-value">{insuredData?.emailAddress}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">KRA PIN:</span>
              <span className="receipt-value">{insuredData?.kraPin}</span>
            </div>
          </div>
        </div>

        <div className="receipt-section">
          <h3>🚗 Vehicle Information</h3>
          <div className="receipt-grid">
            <div className="receipt-item">
              <span className="receipt-label">Registration Number:</span>
              <span className="receipt-value">{vehicleData?.registrationNumber}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Vehicle Make:</span>
              <span className="receipt-value">{vehicleData?.make}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Year of Manufacture:</span>
              <span className="receipt-value">{vehicleData?.yearOfManufacture}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Chassis Number:</span>
              <span className="receipt-value">
                {vehicleData?.chassisNumber 
                  ? maskChassisNumber(String(vehicleData.chassisNumber)) 
                  : 'N/A'}
              </span>
            </div>
            {vehicleData?.engineNumber && (
              <div className="receipt-item">
                <span className="receipt-label">Engine Number:</span>
                <span className="receipt-value">
                  {maskEngineChassisNumber(String(vehicleData.engineNumber))}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="receipt-section">
          <h3>🛡️ Insurance Coverage Details</h3>
          <div className="receipt-grid">
            <div className="receipt-item">
              <span className="receipt-label">Cover Type:</span>
              <span className="receipt-value">{coverType}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Vehicle Class:</span>
              <span className="receipt-value">{selectedClass}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Cover Period:</span>
              <span className="receipt-value">{coverPeriod}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">Start Date:</span>
              <span className="receipt-value">{startDate}</span>
            </div>
            <div className="receipt-item">
              <span className="receipt-label">End Date:</span>
              <span className="receipt-value">{endDate}</span>
            </div>
          </div>
        </div>

        <div className="receipt-section payment-details">
          <h3>💰 Payment Information</h3>
          <div className="payment-summary">
            <div className="payment-row">
              <span className="payment-label">Premium Amount:</span>
              <span className="payment-amount">KES {premium?.amount.toLocaleString()}</span>
            </div>
            <div className="payment-row">
              <span className="payment-label">Payment Method:</span>
              <span className="payment-value">MPESA Mobile Money</span>
            </div>
            <div className="payment-row">
              <span className="payment-label">Transaction Date:</span>
              <span className="payment-value">{currentDate} at {currentTime}</span>
            </div>
          </div>
        </div>

        {/* WhatsApp delivery section commented out - we are not delivering via WhatsApp
        <div className="receipt-section">
          <h3>📱 Certificate Delivery</h3>
          <div className="delivery-info">
            <div className="delivery-icon">📱</div>
            <div className="delivery-message">
              <strong>WhatsApp Delivery:</strong> Your insurance certificate will be sent to <strong>{insuredData?.phoneNumber}</strong> within 2-3 minutes.
            </div>
          </div>
        </div>
        */}

        <div className="receipt-footer">
          <div className="footer-note">
            <p><strong>Important Notes:</strong></p>
            <ul>
              <li>Keep this receipt for your records</li>
              <li>Your policy is active from {startDate} to {endDate}</li>
              <li>For any queries, contact our customer service</li>
              <li>Receipt Code: <strong>{receiptCode}</strong></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Certificate issuance failed – show retry option (when status is failed or we have an error message) */}
      {(certificateStatus === 'failed' || certificateLastError) && onRetryCertificate && (
        <div className="receipt-section" style={{ marginTop: '20px', padding: '16px', background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#856404' }}>⚠️ Certificate not yet issued</h3>
          <p style={{ margin: '0 0 12px 0', color: '#856404' }}>
            Your payment was successful, but the insurance certificate could not be issued. {certificateLastError ? `Error: ${certificateLastError}` : 'You can retry issuance below.'}
          </p>
          <button
            type="button"
            onClick={onRetryCertificate}
            style={{
              background: '#dc3545',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1em',
              fontWeight: 'bold'
            }}
          >
            🔄 Retry certificate issuance
          </button>
        </div>
      )}

      {/* Retry Status Indicator */}
      {(() => {
        const pendingData = localStorage.getItem('bimabot_pending_application');
        if (pendingData) {
          try {
            const data = JSON.parse(pendingData);
            return (
              <div className="retry-status">
                <div className="retry-status-content">
                  <div className="retry-status-icon">⚠️</div>
                  <div className="retry-status-text">
                    <h4>Policy Data Saving</h4>
                    <p>We're still saving your policy data in the background. This may take a few minutes.</p>
                    <p className="retry-status-details">
                      Attempt {data.retryCount + 1} of 8 • Last attempt: {new Date(data.lastAttempt).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                {onRetrySave && (
                  <button className="btn-retry" onClick={onRetrySave}>
                    <span className="btn-icon">🔄</span>
                    <span className="btn-text">Retry Now</span>
                  </button>
                )}
              </div>
            );
          } catch (error) {
            return null;
          }
        }
        return null;
      })()}

      <div className="receipt-actions">
        <button className="btn-secondary" onClick={() => downloadReceipt()}>
          <span className="btn-icon">💾</span>
          <span className="btn-text">Download Receipt</span>
        </button>
        <button className="btn-primary" onClick={onNewApplication}>
          <span className="btn-icon">🚀</span>
          <span className="btn-text">Start New Application</span>
        </button>
      </div>
    </div>
  );
};

export default Receipt;
